import React, { FC, useMemo } from "react";
import "./IssuesHeaderTag.scss";
import { Popover, Tag, Tooltip } from "antd";
import IssuesOverviewPopover from "./issuesOverviewPopover/IssuesOverviewPopover";
import { Annotation } from "../../../annotator/interfaces/annotation";
import { IssueLine, IssuesEntity } from "../../interfaces/issuesEntity";

type ProblemsOverviewProps = {
  disabled: boolean;
  entities: Array<IssuesEntity>;
  errorAttentionAnnotations: Array<Annotation>;
  scoreAttentionAnnotations: Array<Annotation>;
  missingRequiredEntityLines: Array<IssueLine>;
  tagColor: string;
  areThereIssues: boolean;
};

const IssuesHeaderTag: FC<ProblemsOverviewProps> = ({
  disabled,
  entities,
  errorAttentionAnnotations,
  scoreAttentionAnnotations,
  missingRequiredEntityLines,
  tagColor,
  areThereIssues,
}) => {
  const tagLabel = useMemo(() => {
    if (!areThereIssues) {
      return;
    }

    const labels = [];

    const totalErrors =
      errorAttentionAnnotations.length + missingRequiredEntityLines.length;
    if (totalErrors) {
      const errorLabel = totalErrors > 1 ? "errors" : "error";
      labels.push(`${totalErrors} ${errorLabel}`);
    }

    if (scoreAttentionAnnotations?.length) {
      const warningLabel =
        scoreAttentionAnnotations.length > 1 ? "warnings" : "warning";
      labels.push(`${scoreAttentionAnnotations.length} ${warningLabel}`);
    }

    return labels.join(" | ");
    // Depending on the arrays themselves rather than their `.length` only makes
    // this recompute more often; the result is a plain string derived purely
    // from those lengths, so the value handed to the JSX below is unchanged.
  }, [
    areThereIssues,
    errorAttentionAnnotations,
    missingRequiredEntityLines,
    scoreAttentionAnnotations,
  ]);

  if (!areThereIssues || disabled) {
    return null;
  }

  return (
    <div className="issues-overview-container">
      <Popover
        placement="leftBottom"
        className="icon-container"
        title={
          <>
            <h4 className="issues-overview-popover-title">
              Issues in document
            </h4>
            <Tooltip
              title={"Click on an issue to go to the entity automatically"}
            >
              <i className="bi bi-question-circle" />
            </Tooltip>
          </>
        }
        content={
          <IssuesOverviewPopover
            errorAttentionAnnotations={errorAttentionAnnotations}
            scoreAttentionAnnotations={scoreAttentionAnnotations}
            missingRequiredEntityLines={missingRequiredEntityLines}
            entities={entities}
          />
        }
      >
        <Tag color={tagColor} className="issues-overview-tag">
          {tagLabel}
          <i className="bi bi-question-circle"></i>
        </Tag>
      </Popover>
    </div>
  );
};

export default IssuesHeaderTag;
